import { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { api } from './api';
import type { WebhookFailure, WebhookFailureSummary } from './api';
import { providerName } from './pages/Activity';
import { formatDate } from './ui';

/** Under Activity, so the bar and the narrow menu both say where you are. */
export const WEBHOOK_ALERTS_PATH = '/activity/webhooks';

/**
 * A line across the top of every staff page while a webhook for a real payment has gone
 * unused and nobody has looked.
 *
 * This is the whole alert: the app sends no email, and before it a refused webhook was a
 * warning in a log nobody read - which is how every BML webhook was once refused, unseen,
 * while polling quietly covered for them.
 */
export function WebhookAlertBanner() {
  const { pathname } = useLocation();
  const [summary, setSummary] = useState<WebhookFailureSummary | null>(null);

  // On load, on every page change, and when the tab comes back into view. Not on a timer:
  // a request a minute would keep the 12-hour rolling session alive for as long as the tab
  // stayed open, signed in on a PC nobody is at.
  useEffect(() => {
    let cancelled = false;
    const check = () => {
      api.webhookFailures.summary().then(
        (next) => {
          if (!cancelled) setSummary(next);
        },
        // A hint, not a page: a failed check leaves the last answer standing, and the
        // page's own requests report a lost connection or session.
        () => undefined,
      );
    };
    check();
    const onVisible = () => {
      if (document.visibilityState === 'visible') check();
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      cancelled = true;
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [pathname]);

  // The page it points to says all of this and more.
  if (pathname.startsWith(WEBHOOK_ALERTS_PATH)) return null;
  if (!summary || summary.unacknowledged === 0) return null;

  const { unacknowledged, since, latest } = summary;
  const headline =
    unacknowledged === 1
      ? `A webhook could not be used, at ${formatDate(since)}`
      : `${unacknowledged} webhooks could not be used, the first at ${formatDate(since)}`;

  return (
    <div
      role="status"
      className="mb-4 flex flex-wrap items-center gap-x-4 gap-y-2.5 rounded-lg border border-warning/40 bg-warning/10 px-3.5 py-3 sm:mb-5"
    >
      <WarningIcon />
      <div className="min-w-0 flex-1">
        <strong className="block font-semibold text-amber-200">{headline}</strong>
        {latest && (
          <span className="block text-[12.5px] text-amber-100/80 [overflow-wrap:anywhere]">
            {unacknowledged === 1 ? '' : 'Latest: '}
            {failureTitle(latest.kind)} · {failureWhere(latest)}
          </span>
        )}
      </div>
      <Link
        to={WEBHOOK_ALERTS_PATH}
        className="rounded-lg bg-warning/20 px-4 py-2 font-semibold whitespace-nowrap text-amber-100 no-underline hover:bg-warning/30 max-sm:w-full max-sm:text-center"
      >
        Review
      </Link>
    </div>
  );
}

/** What went wrong, in a few words. The server's reason says exactly which check. */
export function failureTitle(kind: string): string {
  switch (kind) {
    case 'signature':
      return 'Signature refused';
    case 'unknown_transaction':
      return 'Payment for a transaction the cloud does not know';
    case 'error':
      return 'Could not be processed';
    default:
      return kind;
  }
}

/** Which provider, and whose: "BML Connect · Ihsaan Company · Main shop · Counter 2". */
export function failureWhere(failure: WebhookFailure): string {
  return [
    providerName(failure.providerCode),
    failure.org?.name,
    failure.outlet?.name,
    failure.counter?.name,
  ]
    .filter(Boolean)
    .join(' · ');
}

function WarningIcon() {
  return (
    <svg
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className="shrink-0 text-warning max-sm:hidden"
      aria-hidden="true"
    >
      <path d="M10.3 3.9 1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0Z" />
      <path d="M12 9v4" />
      <path d="M12 17h.01" />
    </svg>
  );
}
